import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

interface BugStatusPieChartProps {
  resolved: number;
  accepted: number;
  inReview: number;
  rejected: number;
  total: number;
}

interface StatusSlice {
  key: string;
  label: string;
  value: number;
  color: string;
}

interface PiePoint extends StatusSlice {
  percentage: number;
}

interface RechartsTooltipEntry {
  payload: PiePoint;
}

const PIE_INNER_RADIUS = '72%';
const PIE_OUTER_RADIUS = '100%';
const PIE_PADDING_ANGLE = 4;
const PIE_CORNER_RADIUS = 10;

function ChartTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: RechartsTooltipEntry[];
}) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const point = payload[0].payload;

  return (
    <div className="rounded-md bg-gray-900 px-2.5 py-2 text-xs text-white shadow-lg">
      <div className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: point.color }} />
        <span className="font-semibold">{point.label}</span>
      </div>
      <p className="mt-1 text-gray-200">
        {point.value} bugs ({point.percentage}%)
      </p>
    </div>
  );
}

export function BugStatusPieChart({
  resolved,
  inReview,
  rejected, accepted, total
}: BugStatusPieChartProps) {
  const slices: StatusSlice[] = [
    { key: 'accepted', label: 'Accepted', value: accepted, color: '#14AE5C' },
    { key: 'rejected', label: 'Rejected', value: rejected, color: '#EC221F' },
    { key: 'inReview', label: 'In Review', value: inReview, color: '#522504' },
    { key: 'resolved', label: 'Total Resolved', value: resolved, color: '#4F39F6' },
  ];


  const slicesWithPercentage: PiePoint[] = slices
    .map((slice) => ({
      ...slice,
      percentage: total > 0 ? Math.round((slice.value / total) * 100) : 0,
    }));

  const pieData = slicesWithPercentage.filter((slice) => slice.value > 0);

  return (
    <section className="rounded-xl border border-gray-200/60 bg-white p-5 shadow-md shadow-gray-200/40 dark:border-gray-800/50 dark:bg-gray-900">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Resolved Bug Breakdown</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Distribution across workflow stages</p>
        </div>
        <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
          {total} total bugs tracked
        </span>
      </div>

      <div className="flex-center lg:items-center">
        <ul className="space-y-2 w-[20rem]">
          {slicesWithPercentage.map((slice) => (
            <li key={slice.key} className="rounded-md p-1.5">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: slice.color }} />
                  <span className="font-medium text-gray-700 dark:text-gray-200">{slice.label}</span>
                </div>
                <span className="font-semibold text-gray-700 dark:text-gray-200">{slice.value}</span>
              </div>
              <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${slice.percentage}%`, backgroundColor: slice.color }}
                />
              </div>
              <p className="mt-1 text-[11px] font-medium text-gray-500 dark:text-gray-400">{slice.percentage}%</p>
            </li>
          ))}
        </ul>

        <div className="relative mx-auto h-64 w-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={[{ value: 1 }]}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={PIE_INNER_RADIUS}
                outerRadius={PIE_OUTER_RADIUS}
                stroke="none"
                fill="#e5e7eb"
                isAnimationActive={false}
              />

              {pieData.length > 0 && (
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={PIE_INNER_RADIUS}
                  outerRadius={PIE_OUTER_RADIUS}
                  paddingAngle={PIE_PADDING_ANGLE}
                  cornerRadius={PIE_CORNER_RADIUS}
                  stroke="none"
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.key} fill={entry.color} />
                  ))}
                </Pie>
              )}

              <Tooltip content={<ChartTooltip />} cursor={false} wrapperStyle={{ outline: 'none' }} />
            </PieChart>
          </ResponsiveContainer>

          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
               Resolved Bugs
            </span>
            <span className="text-3xl font-semibold text-gray-900 dark:text-white">{resolved}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
